import React, { useMemo } from 'react';
import classNames from 'classnames';
import { ConnectedUser } from '../../../stores/types/connection';
import { NetworkCondition } from '../../../stores/types/opentok-health';
import { RESTRICTED_USER_STATES } from '../../../stores/reducers/session';
import { sessionRows, SessionRow, SOURCE_LABELS } from '../session-streams';
import { StreamMeasurement, UseSessionMonitorResult } from '../../../hooks/health/use-session-monitor';
import ConditionPill from '../condition-pill';
import { formatElapsed } from '../format';

interface Props {
	users: ConnectedUser[];
	present: Set<number>; /** Of those, who the roster still has in the room. */
	monitor: UseSessionMonitorResult;
	selected: string | null;
	onSelect: (streamId: string | null) => void;
}

const RESTRICTED_LABELS: Record<number, string> = {
	[RESTRICTED_USER_STATES.CAMERA]: 'camera blocked',
	[RESTRICTED_USER_STATES.MICROPHONE]: 'mic blocked',
	[RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE]: 'cam + mic blocked',
	[RESTRICTED_USER_STATES.BLOCKED]: 'blocked',
	[RESTRICTED_USER_STATES.RECONNECTING]: 'reconnecting',
};

/** Said in full once rather than abbreviated on every row that carries one. */
const DISCREPANCY_NOTES: Record<string, string> = {
	'no-stream': 'this client reports a publisher the session has no stream for',
	unclaimed: 'no client in this room claims this stream',
};

/**
 * How long a live audio track has to stay silent before it is worth calling a
 * dead mic. Long enough that a pause in the conversation does not trip it.
 */
const DEAD_MIC_SILENCE_MS = 30000;

/** The stream name only carries a gr_user, so the roster supplies the person. */
const nameFor = (user?: ConnectedUser): string | undefined => (
	user ? `${user.first_name} ${user.last_name}`.trim() : undefined
);

/** Percentage, not a ratio: 0.031 means nothing at a glance. */
const asPercent = (ratio?: number): string => (
	typeof ratio === 'number' ? `${(ratio * 100).toFixed(1)}%` : '—'
);

function PublisherRow({
	row, measurement, selected, onSelect,
}: {
	row: SessionRow;
	measurement?: StreamMeasurement;
	selected: boolean;
	onSelect: () => void;
}) {
	const owner = nameFor(row.user);
	const deadMic = row.hasAudio
		&& typeof measurement?.silentForMs === 'number'
		&& measurement.silentForMs > DEAD_MIC_SILENCE_MS;
	const restricted = row.user?.restrictedUserState
		? RESTRICTED_LABELS[row.user.restrictedUserState]
		: undefined;
	const sourceLabel = SOURCE_LABELS[row.source];

	return (
		<tr
			className={classNames({
				'health-row--selected': selected,
				'health-row--warn': !!row.discrepancy,
				'health-row--selectable': !!row.streamId,
			})}
			onClick={row.streamId ? onSelect : undefined}
		>
			<td className='health-cell--identity'>
				<div className='health-identity'>
					<span className='health-truncate'>
						{owner ?? <span className='health-empty'>unknown</span>}
					</span>
					{row.departed && <span className='health-tag health-tag--warn'>left</span>}
					{sourceLabel && <span className='health-tag'>{sourceLabel}</span>}
					{measurement?.codec && <span className='health-tag'>{measurement.codec}</span>}
				</div>
				<div className='health-identity health-subtle health-mono'>
					<span className='health-truncate' title={row.streamId}>
						{row.streamId ?? 'no stream'}
					</span>
					{!!row.creationTime && <span>· {formatElapsed(row.creationTime)}</span>}
				</div>
			</td>
			<td>
				<div className='health-tagged'>
					{row.user ? (
						<>
							{!row.user.in_room && <span className='health-tag health-tag--warn'>not in room</span>}
							{row.user.watcher_only && <span className='health-tag'>watcher only</span>}
							{restricted && <span className='health-tag health-tag--warn'>{restricted}</span>}
							{row.user.isProducer && <span className='health-tag'>producer</span>}
						</>
					) : <span className='health-empty'>not reporting</span>}
					{row.discrepancy && (
						<span className='health-tag health-tag--warn'>{row.discrepancy}</span>
					)}
				</div>
			</td>
			<td className='health-stack'>
				<div>
					video {row.hasVideo ? 'on' : 'off'}
					{row.hasVideo && !!row.width && (
						<span className='health-qualifier'>{` · ${row.width}x${row.height}`}</span>
					)}
				</div>
				<div className={classNames({ 'health-error-value': deadMic })}>
					audio {row.hasAudio ? 'on' : 'off'}
					{deadMic && ' · no signal'}
				</div>
			</td>
			<td>
				<div className='health-tagged'>
					{measurement
						? <ConditionPill condition={measurement.uplink as NetworkCondition} />
						: <span className='health-empty'>not measured</span>}
					{measurement?.uplinkReason && measurement.uplinkReason !== 'none' && (
						<span className='health-tag health-tag--warn'>{measurement.uplinkReason}</span>
					)}
					{measurement && !measurement.uplink && measurement.senderStatsAvailable === false && (
						<span className='health-tag'>not reported</span>
					)}
				</div>
			</td>
			<td className='health-mono health-stack'>
				<div className={classNames({
					'health-error-value': (measurement?.videoPacketLoss ?? 0) > 0.02,
				})}
				>
					{measurement?.videoKbps ?? '—'} kbps · {asPercent(measurement?.videoPacketLoss)} video
				</div>
				<div className={classNames({
					'health-error-value': (measurement?.audioPacketLoss ?? 0) > 0.02,
				})}
				>
					{measurement?.audioKbps ?? '—'} kbps · {asPercent(measurement?.audioPacketLoss)} audio
				</div>
			</td>
			<td className='health-mono health-stack'>
				<div>{measurement?.frameRate ?? '—'} fps arriving</div>
				{typeof measurement?.decodedFrameRate === 'number' && (
					<div>{measurement.decodedFrameRate} fps decoded</div>
				)}
			</td>
			<td className='health-mono health-stack'>
				<div>{measurement?.videoMos?.toFixed(1) ?? '—'} video · {measurement?.audioMos?.toFixed(1) ?? '—'} audio</div>
				<div>
					{measurement?.freezeCount ?? 0} freeze{measurement?.freezeCount === 1 ? '' : 's'}
					{!!measurement?.freezeSeconds && (
						<span className='health-qualifier'>{` · ${measurement.freezeSeconds.toFixed(1)}s`}</span>
					)}
				</div>
			</td>
		</tr>
	);
}

/**
 * One row per publisher, joining what the client reports, what the media server
 * has, and what a subscription to it measures.
 *
 * The three used to be separate tables keyed three different ways, so the
 * reader joined them by eye - and the case worth catching, a client reporting a
 * publisher the session has no stream for, was visible to nobody.
 */
export default function SessionStreamsPanel({
	users, present, monitor, selected, onSelect,
}: Props) {
	const rows = useMemo(
		() => sessionRows({
			streams: monitor.streams, users, present, connected: monitor.connected,
		}),
		[monitor.streams, users, present, monitor.connected],
	);
	const byStream = useMemo(
		() => new Map(monitor.measurements.map((entry) => [entry.streamId, entry])),
		[monitor.measurements],
	);

	const discrepancies = useMemo(
		() => [...new Set(rows.map((row) => row.discrepancy).filter(Boolean))] as string[],
		[rows],
	);

	return (
		<section className='health-panel'>
			<header className='health-panel__header'>
				<div>
					<h3 className='health-panel__title'>Publishers</h3>
					<span className='health-panel__source'>opentok</span>
				</div>
				<div className='health-panel__status'>
					<span className={classNames('health-pill', rows.length ? 'health-pill--ok' : 'health-pill--empty')}>
						{rows.length} publisher{rows.length === 1 ? '' : 's'}
					</span>
				</div>
			</header>

			<div className='health-panel__body'>
				{!rows.length && (
					<p className='health-panel__note'>Nobody is publishing into this session.</p>
				)}

				{!!rows.length && (
					<>
						<table className='health-table health-table--compact'>
							<thead>
								<tr>
									<th>Publisher</th>
									<th>Client</th>
									<th>Carrying</th>
									<th>Publisher uplink</th>
									<th>Received</th>
									<th>Frame rate</th>
									<th>Playback</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row: SessionRow) => (
									<PublisherRow
										key={row.streamId ?? `no-stream-${row.grUser}`}
										row={row}
										measurement={row.streamId ? byStream.get(row.streamId) : undefined}
										selected={!!row.streamId && row.streamId === selected}
										onSelect={() => onSelect(
											row.streamId === selected ? null : row.streamId ?? null,
										)}
									/>
								))}
							</tbody>
						</table>

						{discrepancies.map((kind) => (
							<p key={kind} className='health-subtle'>
								<strong>{kind}</strong> — {DISCREPANCY_NOTES[kind]}
							</p>
						))}

						{selected && (
							<p className='health-subtle'>
								Session events below are filtered to the selected publisher.
							</p>
						)}
					</>
				)}
			</div>
		</section>
	);
}
